import { useEffect, useMemo, useState } from 'react';
import { Check, Home, LogIn, LogOut, Menu, Pencil, Plus, Trash2, WandSparkles, X } from 'lucide-react';
import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithPopup,
  signOut,
  updatePassword,
} from 'firebase/auth';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, cloudFunctions, db, googleProvider } from './firebase';
import {
  getFriendlyAuthErrorMessage,
  getPasswordValidationMessage,
  isPasswordUser,
  sendPasswordReset,
  signInWithVerifiedEmailPassword,
  upsertUserInFirestore,
} from './authHelpers';
import { defaultBillingInfoText, pickSiteContent } from './siteContent';
import {
  formatDateKeyInBudapest,
  formatDateTimeInBudapest,
  getBudapestDateAtTime,
  getBudapestTodayKey,
  getBudapestTrainingDeadlineDate,
} from './dateUtils';
import {
  buildBestResultsByUser,
  formatExerciseTask,
  formatResultValue,
  getExerciseName,
  getPublicPlayerName,
  getResultSortValue,
  getTimestampMillis,
  normalizeExercise,
  normalizeFitnessResult,
  normalizePersonalRecord,
  sortRankingRows,
} from './resultsUtils';
import {
  buildSquashHeadToHeadRows,
  getSquashSetLoserScore,
  getSquashSetPlayerName,
  isValidSquashSetLoserScore,
  normalizeSquashSet,
  sortSquashSetsNewestFirst,
} from './squashSetUtils';
import SquashScoreEditor from './SquashScoreEditor';

const getTextField = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

let nextSquashSetDraftId = 1;
const createSquashSetDraft = () => ({
  id: `set-draft-${nextSquashSetDraftId++}`,
  winnerId: '',
  loserScore: '',
});

const formatSquashPointAverage = (value) => (
  Number.isFinite(value)
    ? value.toLocaleString('hu-HU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '–'
);

const normalizeTrainingTaskAssignment = (assignmentDoc) => {
  const data = assignmentDoc.data() || {};
  return {
    id: assignmentDoc.id,
    taskName: getTextField(data.task_name) || 'Ismeretlen feladat',
    taskCategory: getTextField(data.task_category),
    quantity: Number(data.quantity) || 0,
    unit: getTextField(data.unit),
    description: getTextField(data.description),
    position: Number.isFinite(data.position) ? data.position : Number.MAX_SAFE_INTEGER,
    createdAt: data.createdAt,
  };
};

const getUserAddress = (userData = {}) => {
  const nestedAddress = userData.address && typeof userData.address === 'object' ? userData.address : {};

  const country = getTextField(
    userData.billing_country,
    userData.country,
    userData.address_country,
    nestedAddress.country,
  );
  const zip = getTextField(
    userData.billing_zip,
    userData.postal_code,
    userData.zip,
    userData.zipcode,
    userData.address_zip,
    nestedAddress.zip,
    nestedAddress.postal_code,
  );
  const city = getTextField(
    userData.billing_city,
    userData.city,
    userData.address_city,
    nestedAddress.city,
  );
  const street = getTextField(
    userData.billing_address,
    userData.street,
    userData.address_line1,
    userData.address_street,
    nestedAddress.street,
    nestedAddress.line1,
  );

  return { country, zip, city, street };
};

const getUserBillingName = (userData = {}) =>
  getTextField(userData.billing_name, userData.billingName, userData.invoice_name);

const hasAnyAddressData = (address) =>
  Boolean(address.name || address.country || address.zip || address.city || address.street);

const hasCompleteBillingData = (address) =>
  Boolean(address.name && address.country && address.zip && address.city && address.street);

const DEFAULT_BILLING_COUNTRY = 'Magyarország';

const cityByPostalCode = {
  2000: 'Szentendre',
  2011: 'Budakalász',
  2030: 'Érd',
  2040: 'Budaörs',
  2045: 'Törökbálint',
  2051: 'Biatorbágy',
  2100: 'Gödöllő',
  2112: 'Veresegyház',
  2120: 'Dunakeszi',
  2131: 'Göd',
  2141: 'Csömör',
  2151: 'Fót',
  2200: 'Monor',
  2300: 'Ráckeve',
  2310: 'Szigetszentmiklós',
  2330: 'Dunaharaszti',
  2400: 'Dunaújváros',
  2500: 'Esztergom',
  2600: 'Vác',
  2700: 'Cegléd',
  2800: 'Tatabánya',
  2890: 'Tata',
  3000: 'Hatvan',
  3100: 'Salgótarján',
  3200: 'Gyöngyös',
  3300: 'Eger',
  3400: 'Mezőkövesd',
  3525: 'Miskolc',
  3600: 'Ózd',
  3700: 'Kazincbarcika',
  3900: 'Szerencs',
  3980: 'Sátoraljaújhely',
  4024: 'Debrecen',
  4100: 'Berettyóújfalu',
  4200: 'Hajdúszoboszló',
  4220: 'Hajdúböszörmény',
  4300: 'Nyírbátor',
  4400: 'Nyíregyháza',
  4600: 'Kisvárda',
  4700: 'Mátészalka',
  5000: 'Szolnok',
  5100: 'Jászberény',
  5200: 'Törökszentmiklós',
  5300: 'Karcag',
  5400: 'Mezőtúr',
  5600: 'Békéscsaba',
  5700: 'Gyula',
  5800: 'Mezőkovácsháza',
  5900: 'Orosháza',
  6000: 'Kecskemét',
  6100: 'Kiskunfélegyháza',
  6200: 'Kiskőrös',
  6300: 'Kalocsa',
  6400: 'Kiskunhalas',
  6500: 'Baja',
  6600: 'Szentes',
  6700: 'Szeged',
  6800: 'Hódmezővásárhely',
  6900: 'Makó',
  7000: 'Sárbogárd',
  7100: 'Szekszárd',
  7200: 'Dombóvár',
  7300: 'Komló',
  7400: 'Kaposvár',
  7500: 'Nagyatád',
  7600: 'Pécs',
  7700: 'Mohács',
  7800: 'Siklós',
  7900: 'Szigetvár',
  8000: 'Székesfehérvár',
  8100: 'Várpalota',
  8200: 'Veszprém',
  8300: 'Tapolca',
  8400: 'Ajka',
  8500: 'Pápa',
  8600: 'Siófok',
  8700: 'Marcali',
  8800: 'Nagykanizsa',
  8900: 'Zalaegerszeg',
  9021: 'Győr',
  9200: 'Mosonmagyaróvár',
  9300: 'Csorna',
  9400: 'Sopron',
  9500: 'Celldömölk',
  9600: 'Sárvár',
  9700: 'Szombathely',
  9900: 'Körmend',
};

const getCityByHungarianPostalCode = (value) => {
  const normalized = String(value || '').replace(/\D/g, '').slice(0, 4);
  if (normalized.length !== 4) {
    return '';
  }

  const numericZip = Number(normalized);
  if (numericZip >= 1011 && numericZip <= 1239) {
    return 'Budapest';
  }

  return cityByPostalCode[normalized] || cityByPostalCode[numericZip] || '';
};

const formatTrainingDate = (dateKey) => {
  if (typeof dateKey !== 'string') {
    return 'Ismeretlen dátum';
  }

  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) {
    return dateKey;
  }

  return formatDateKeyInBudapest(dateKey, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });
};

const formatRegistrationTimestamp = (timestampValue) => {
  if (!timestampValue?.toDate) {
    return 'Nincs adat';
  }
  return formatDateTimeInBudapest(timestampValue.toDate(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTrainingTimeRange = (registration) => {
  const startDate = getRegistrationStartDate(registration);
  if (!startDate) {
    return 'Nincs adat';
  }

  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  const formatTime = (date) => formatDateTimeInBudapest(date, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${formatTime(startDate)} – ${formatTime(endDate)}`;
};

const getTrainingStartDateFromKey = (trainingDateKey) => {
  if (typeof trainingDateKey !== 'string') {
    return null;
  }

  const [year, month, day] = trainingDateKey.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return getBudapestDateAtTime(trainingDateKey, 9);
};

const getRegistrationStartDate = (registration) => {
  if (registration?.trainingStartAt?.toDate) {
    return registration.trainingStartAt.toDate();
  }
  return getTrainingStartDateFromKey(registration?.trainingDate || '');
};

const canCancelTraining = (trainingDateKey) => {
  const deadline = getBudapestTrainingDeadlineDate(trainingDateKey, 15);
  return deadline ? new Date() < deadline : false;
};

const nicknameIdeas = [
  'Fiatal Titán',
  'Gömbvillám',
  'Pályaördög',
  'Fonákfőnök',
  'Gumilábú Stratég',
  'Szervakirály',
  'Falharapó Bajnok',
  'Ütőmágus',
  'Csúszásprofesszor',
  'Reaktor Kapitány',
  'Villámcsukló',
  'Taktikai Tornádó',
  'Pályaszéli Filozófus',
  'Labdazsonglőr',
  'Csendes Romboló',
  'Ütős Ügyosztály',
  'Sarokvadász',
  'Lendületlovag',
  'Tempózsonglőr',
  'Mosolygó Terminátor',
  'Trükkös Titkosfegyver',
  'Kondenzcsík Kapitány',
  'Mini Hurrikán',
  'Pattanásügyi Miniszter',
  'Izzadtságmenedzser',
  'Csuklóból Veszélyes',
  'Pályaporszívó',
  'Derűs Daráló',
  'Ütővel Tárgyaló',
  'Squash-Kommandós',
  'Sarokból Visszatérő',
  'Hirtelen Hőhullám',
  'Kis Kavarógép',
  'Nagy Mentésügyi Referens',
  'Falbarát Főtiszt',
  'Tempócsászár',
  'Ütőforgató Fenomén',
  'Szervaosztó',
  'Váratlan Visszapattanó',
  'Kétségbeesett Zseni',
  'Nyugodt Vihar',
  'Könnyed Káosz',
  'Mosolygó Rakéta',
  'Pályaügyi Államtitkár',
  'Labdaterelő Mester',
  'Ütőpárbaj Lovagja',
  'Fáradhatatlan Csoszogó',
  'Halk Hurrikán',
  'Nagyívű Mentőakció',
  'Későn Ébredő Villám',
  'Saroklakó Legendárium',
  'Lábmunkaügyi Biztos',
  'Fonákból Bölcs',
  'Tenyeres Tűzijáték',
  'Pálya Pumája',
  'Térfél Tábornok',
  'Ütővel Érvelő',
  'Surranó Szerva',
  'Moccanó Meteor',
  'Taktikai Krumplipüré',
  'Vidám Visszavágó',
  'Gyanúsan Gyors',
  'Kalandvágyó Kontra',
  'Hátsófal Hőse',
  'Frontfal Főnix',
  'Röptés Ravaszdi',
  'Pattanó Parancsnok',
  'Csillogó Csukló',
  'Türelem Tornádója',
  'Kapkodó Kométa',
  'Elegáns Erőgép',
  'Szerva Sámán',
  'Mélységi Mentő',
  'Térfél Türelmetlenje',
  'Pályarajzoló Művész',
  'Álmos Akadály',
  'Derűs Defenzív',
  'Ritmusvadász',
  'Kanyarított Kalandor',
  'Falcirkusz Igazgató',
  'Ütőszelídítő',
  'Pörgő Pergődob',
  'Visszapattanás Vezér',
  'Tűpontos Tévelygő',
  'Királyi Keresztlabda',
  'Rafinált Röpte',
  'Hősies Hátrálás',
  'Támadó Tündöklés',
  'Lassú Láva',
  'Gyors Gőzhenger',
  'Kecses Katasztrófa',
  'Fáradt Főnix',
  'Csuklós Csillagromboló',
  'Pályaközepi Páholy',
  'Örök Mozgó',
  'Hátfal Harcosa',
  'Frontfal Figyelő',
  'Sas Tekintet',
  'Labdaidomár',
  'Szerva Szobrász',
  'Fonák Főmérnök',
  'Tenyeres Tanácsadó',
  'Pályaszagú Professzor',
  'Térfél Tündér',
  'Sportos Szélvész',
  'Kedves Kíméletlen',
  'Barátságos Buldózer',
  'Rövidlabda Rémálma',
  'Hosszúlabda Hőse',
  'Pontmentő Parádé',
  'Meccslabda Mágnes',
  'Cseles Csillag',
  'Lendületes Lecsó',
  'Pálya Picasso',
  'Ziccer Zsonglőr',
  'Vonalvadász',
  'Taktikai Tündöklés',
  'Sarokszelídítő',
  'Futószalag Fantom',
  'Napsütötte Nyomás',
  'Edzésnapló Elit',
  'Hétvégi Harcos',
  'Reggeli Rakéta',
  'Kávémentes Kommandó',
  'Pontvadász Pimasz',
  'Türelmes Támadó',
  'Átlátható Ármány',
  'Légies Lendület',
  'Gyakorlat Gépész',
  'Mosolygó Megmentő',
  'Pálya Poétája',
  'Visszaérő Varázslat',
];

const formatNickname = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(/([\s-]+)/u)
    .map((part) => {
      if (!part || /^[\s-]+$/u.test(part)) {
        return part;
      }
      const letters = Array.from(part.toLocaleLowerCase('hu-HU'));
      if (letters.length === 0) {
        return '';
      }
      return `${letters[0].toLocaleUpperCase('hu-HU')}${letters.slice(1).join('')}`;
    })
    .join('');

const getNicknameKey = (value) =>
  formatNickname(value)
    .toLocaleLowerCase('hu-HU')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getRandomNickname = (currentNickname = '', reservedNicknameKeys = new Set()) => {
  const currentKey = getNicknameKey(currentNickname);
  const availableNicknames = nicknameIdeas.filter((nickname) => {
    const nicknameKey = getNicknameKey(nickname);
    return nicknameKey && nicknameKey !== currentKey && !reservedNicknameKeys.has(nicknameKey);
  });

  if (availableNicknames.length === 0) {
    return '';
  }

  return availableNicknames[Math.floor(Math.random() * availableNicknames.length)];
};

const sortRegistrationsAscending = (left, right) => {
  const leftStart = getRegistrationStartDate(left);
  const rightStart = getRegistrationStartDate(right);

  if (leftStart && rightStart) {
    return leftStart.getTime() - rightStart.getTime();
  }
  if (leftStart) {
    return -1;
  }
  if (rightStart) {
    return 1;
  }
  return (left.trainingDate || '').localeCompare(right.trainingDate || '');
};

const defaultEmailPreferences = {
  registrationConfirmation: true,
  userCancellation: true,
  adminTrainingCancellation: true,
  adminRegistrationRemoval: true,
  trainingReminder: true,
};

const initialPasswordChangeForm = {
  currentPassword: '',
  nextPassword: '',
  nextPasswordConfirm: '',
};

const emailPreferenceOptions = [
  {
    key: 'registrationConfirmation',
    title: 'Jelentkezés visszaigazolása',
    description: 'E-mail és naptármeghívó sikeres edzésjelentkezés után.',
  },
  {
    key: 'userCancellation',
    title: 'Saját lemondás visszaigazolása',
    description: 'E-mail és naptárfrissítés, amikor lemondod a saját jelentkezésed.',
  },
  {
    key: 'adminTrainingCancellation',
    title: 'Szervezői edzéslemondás',
    description: 'Értesítés, amikor egy teljes edzés alkalmat a szervező mond le.',
  },
  {
    key: 'adminRegistrationRemoval',
    title: 'Admin által törölt jelentkezés',
    description: 'Értesítés, amikor az admin törli a jelentkezésed vagy várólistás helyed.',
  },
  {
    key: 'trainingReminder',
    title: 'Szabad hely emlékeztető',
    description: 'E-mail 48 órával a következő edzés előtt, ha még van szabad hely.',
  },
];

const pickEmailPreferences = (source = {}) => {
  const storedPreferences =
    source.email_preferences && typeof source.email_preferences === 'object'
      ? source.email_preferences
      : {};

  return Object.fromEntries(
    Object.entries(defaultEmailPreferences).map(([key, defaultValue]) => [
      key,
      typeof storedPreferences[key] === 'boolean' ? storedPreferences[key] : defaultValue,
    ]),
  );
};

function ProfilePage() {
  const [billingForm, setBillingForm] = useState({
    name: '',
    country: DEFAULT_BILLING_COUNTRY,
    zip: '',
    city: '',
    street: '',
  });
  const [nicknameForm, setNicknameForm] = useState('');
  const [nicknamePanelOpen, setNicknamePanelOpen] = useState(false);
  const [nicknameLoading, setNicknameLoading] = useState(false);
  const [nicknameSaveBusy, setNicknameSaveBusy] = useState(false);
  const [nicknameSaveNotice, setNicknameSaveNotice] = useState('');
  const [reservedNicknameKeys, setReservedNicknameKeys] = useState(() => new Set());
  const [billingPanelOpen, setBillingPanelOpen] = useState(false);
  const [resultsPanelOpen, setResultsPanelOpen] = useState(false);
  const [tasksPanelOpen, setTasksPanelOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [profileManagementPanelOpen, setProfileManagementPanelOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingSaveBusy, setBillingSaveBusy] = useState(false);
  const [billingSaveNotice, setBillingSaveNotice] = useState('');
  const [emailPreferences, setEmailPreferences] = useState(defaultEmailPreferences);
  const [emailPreferencesLoading, setEmailPreferencesLoading] = useState(false);
  const [emailPreferencesSaveBusy, setEmailPreferencesSaveBusy] = useState(false);
  const [emailPreferencesNotice, setEmailPreferencesNotice] = useState('');
  const [accountDeleteBusy, setAccountDeleteBusy] = useState(false);
  const [accountDeleteNotice, setAccountDeleteNotice] = useState('');
  const [accountDeleteConfirmOpen, setAccountDeleteConfirmOpen] = useState(false);
  const [passwordChangeForm, setPasswordChangeForm] = useState(initialPasswordChangeForm);
  const [passwordChangeBusy, setPasswordChangeBusy] = useState(false);
  const [passwordChangeNotice, setPasswordChangeNotice] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authNotice, setAuthNotice] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [passwordManagedByGoogleNoticeOpen, setPasswordManagedByGoogleNoticeOpen] = useState(false);
  const [profileActionsOpen, setProfileActionsOpen] = useState(false);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [selectedTaskTrainingDate, setSelectedTaskTrainingDate] = useState('');
  const [trainingTaskAssignments, setTrainingTaskAssignments] = useState([]);
  const [trainingTaskAssignmentsLoading, setTrainingTaskAssignmentsLoading] = useState(false);
  const [completedTrainingTaskIds, setCompletedTrainingTaskIds] = useState(() => new Set());
  const [taskCompletionBusyId, setTaskCompletionBusyId] = useState('');
  const [taskCompletionNotice, setTaskCompletionNotice] = useState('');
  const [cancelBusyDate, setCancelBusyDate] = useState('');
  const [cancelNotice, setCancelNotice] = useState('');
  const [lateCancelOpen, setLateCancelOpen] = useState(false);
  const [trainingWeeks, setTrainingWeeks] = useState({});
  const [billingInfoText, setBillingInfoText] = useState(defaultBillingInfoText);
  const [exercises, setExercises] = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [fitnessResults, setFitnessResults] = useState([]);
  const [fitnessResultsLoading, setFitnessResultsLoading] = useState(false);
  const [personalRecords, setPersonalRecords] = useState([]);
  const [allTimeRecords, setAllTimeRecords] = useState([]);
  const [leaderboardResults, setLeaderboardResults] = useState([]);
  const [resultSaveBusy, setResultSaveBusy] = useState(false);
  const [resultNotice, setResultNotice] = useState('');
  const [resultsMode, setResultsMode] = useState('exercises');
  const [squashCurrentPlayer, setSquashCurrentPlayer] = useState(null);
  const [squashOpponents, setSquashOpponents] = useState([]);
  const [squashOpponentsLoading, setSquashOpponentsLoading] = useState(false);
  const [squashTrainingDate, setSquashTrainingDate] = useState('');
  const [selectedSquashOpponentId, setSelectedSquashOpponentId] = useState('');
  const [squashOpponentConfirmed, setSquashOpponentConfirmed] = useState(false);
  const [squashSetDrafts, setSquashSetDrafts] = useState(() => [createSquashSetDraft()]);
  const [squashSetSaveBusy, setSquashSetSaveBusy] = useState(false);
  const [squashSetNotice, setSquashSetNotice] = useState('');
  const [squashSets, setSquashSets] = useState([]);
  const [squashSetsLoading, setSquashSetsLoading] = useState(false);
  const [squashSetEditDrafts, setSquashSetEditDrafts] = useState({});
  const [squashSetBusyId, setSquashSetBusyId] = useState('');
  const [resultForm, setResultForm] = useState({
    exerciseId: '',
    trainingDate: '',
    value: '',
  });
  const [leaderboardForm, setLeaderboardForm] = useState({
    exerciseId: '',
    trainingDate: '',
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && isPasswordUser(user) && !user.emailVerified) {
        setCurrentUser(null);
        setAuthNotice('A belépéshez előbb erősítsd meg az e-mail címed.');
        void signOut(auth);
        return;
      }

      setCurrentUser(user);
      setAuthNotice('');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const contentRef = doc(db, 'site_content', 'main');
    const unsubscribe = onSnapshot(
      contentRef,
      (snapshot) => {
        const remoteContent = snapshot.exists() ? snapshot.data() : {};
        const pickedContent = pickSiteContent(remoteContent);
        setBillingInfoText(pickedContent.billingInfoText || defaultBillingInfoText);
      },
      () => {
        setBillingInfoText(defaultBillingInfoText);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setBillingForm({
      name: '',
      country: DEFAULT_BILLING_COUNTRY,
      zip: '',
      city: '',
      street: '',
    });
    setNicknameForm('');
    setNicknamePanelOpen(false);
    setNicknameLoading(false);
    setNicknameSaveBusy(false);
    setNicknameSaveNotice('');
    setReservedNicknameKeys(new Set());
    setBillingPanelOpen(false);
    setResultsPanelOpen(false);
    setTasksPanelOpen(false);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(false);
    setBillingLoading(false);
    setBillingLoaded(false);
    setBillingSaveBusy(false);
    setBillingSaveNotice('');
    setEmailPreferences(defaultEmailPreferences);
    setEmailPreferencesLoading(false);
    setEmailPreferencesSaveBusy(false);
    setEmailPreferencesNotice('');
    setAccountDeleteBusy(false);
    setAccountDeleteNotice('');
    setAccountDeleteConfirmOpen(false);
    setPasswordChangeForm(initialPasswordChangeForm);
    setPasswordChangeBusy(false);
    setPasswordChangeNotice('');
    setPasswordManagedByGoogleNoticeOpen(false);
    setSelectedTaskTrainingDate('');
    setTrainingTaskAssignments([]);
    setTrainingTaskAssignmentsLoading(false);
    setCompletedTrainingTaskIds(new Set());
    setTaskCompletionBusyId('');
    setTaskCompletionNotice('');
    setResultsMode('exercises');
    setSquashCurrentPlayer(null);
    setSquashOpponents([]);
    setSquashOpponentsLoading(false);
    setSquashTrainingDate('');
    setSelectedSquashOpponentId('');
    setSquashOpponentConfirmed(false);
    setSquashSetDrafts([createSquashSetDraft()]);
    setSquashSetSaveBusy(false);
    setSquashSetNotice('');
    setSquashSets([]);
    setSquashSetsLoading(false);
    setSquashSetEditDrafts({});
    setSquashSetBusyId('');
    setLeaderboardForm({
      exerciseId: '',
      trainingDate: '',
    });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setNicknameForm('');
      setNicknameLoading(false);
      return undefined;
    }

    setNicknameLoading(true);
    setEmailPreferencesLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setNicknameForm(formatNickname(getTextField(data.nickname, data.nickName, data.becenev)));
        setEmailPreferences(pickEmailPreferences(data));
        setNicknameLoading(false);
        setEmailPreferencesLoading(false);
      },
      (error) => {
        setNicknameSaveNotice(`Becenév betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setNicknameLoading(false);
        setEmailPreferencesNotice(`Értesítések betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setEmailPreferencesLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setReservedNicknameKeys(new Set());
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'nicknames'),
      (snapshot) => {
        const keys = new Set();
        snapshot.forEach((nicknameDoc) => {
          const data = nicknameDoc.data();
          if (data.user_id !== currentUser.uid) {
            keys.add(nicknameDoc.id);
          }
        });
        setReservedNicknameKeys(keys);
      },
      () => {
        setReservedNicknameKeys(new Set());
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setRegistrations([]);
      setRegistrationsLoading(false);
      return undefined;
    }

    setRegistrationsLoading(true);
    const registrationsQuery = query(
      collection(db, 'training_registrations'),
      where('user_id', '==', currentUser.uid),
    );

    const unsubscribe = onSnapshot(
      registrationsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((registrationDoc) => {
          const data = registrationDoc.data();
          items.push({
            id: registrationDoc.id,
            trainingDate: typeof data.training_date === 'string' ? data.training_date : '',
            trainingStartAt: data.training_start_at,
            createdAt: data.createdAt,
            lateCancelled: data.late_cancelled === true,
            lateCancelledAt: data.late_cancelled_at,
          });
        });

        setRegistrations(items);
        setRegistrationsLoading(false);
      },
      (error) => {
        setAuthNotice(`Jelentkezések betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setRegistrationsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setExercises([]);
      setExercisesLoading(false);
      return undefined;
    }

    setExercisesLoading(true);
    const exercisesQuery = query(collection(db, 'fitness_exercises'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(
      exercisesQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((exerciseDoc) => {
          const exercise = normalizeExercise(exerciseDoc);
          if (exercise.active) {
            items.push(exercise);
          }
        });
        setExercises(items);
        setExercisesLoading(false);
      },
      (error) => {
        setAuthNotice(`Gyakorlatok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setExercisesLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setFitnessResults([]);
      setFitnessResultsLoading(false);
      return undefined;
    }

    setFitnessResultsLoading(true);
    const resultsQuery = query(
      collection(db, 'fitness_results'),
      where('user_id', '==', currentUser.uid),
    );
    const unsubscribe = onSnapshot(
      resultsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((resultDoc) => {
          items.push(normalizeFitnessResult(resultDoc));
        });
        setFitnessResults(items);
        setFitnessResultsLoading(false);
      },
      (error) => {
        setAuthNotice(`Eredmények betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setFitnessResultsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setSquashSets([]);
      setSquashSetsLoading(false);
      return undefined;
    }

    setSquashSetsLoading(true);
    const setsQuery = query(
      collection(db, 'squash_sets'),
      where('player_ids', 'array-contains', currentUser.uid),
    );
    const unsubscribe = onSnapshot(
      setsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((setDoc) => {
          items.push(normalizeSquashSet(setDoc));
        });
        items.sort(sortSquashSetsNewestFirst);
        setSquashSets(items);
        setSquashSetsLoading(false);
      },
      (error) => {
        setSquashSetNotice(`Szett eredmények betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setSquashSets([]);
        setSquashSetsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !resultsPanelOpen || resultsMode !== 'sets') {
      return undefined;
    }

    let cancelled = false;
    const loadOpponents = async () => {
      setSquashOpponentsLoading(true);
      setSquashSetNotice('');
      try {
        const getOpponents = httpsCallable(cloudFunctions, 'getTodaySquashSetOpponents');
        const response = await getOpponents();
        if (cancelled) {
          return;
        }
        const data = response.data || {};
        setSquashCurrentPlayer(data.currentPlayer || {
          id: currentUser.uid,
          displayName: currentUser.displayName || currentUser.email || 'Saját játékos',
          nickname: '',
          publicName: currentUser.displayName || currentUser.email || 'Saját játékos',
        });
        setSquashOpponents(Array.isArray(data.opponents) ? data.opponents : []);
        setSquashTrainingDate(typeof data.trainingDate === 'string' ? data.trainingDate : '');
      } catch (error) {
        if (!cancelled) {
          setSquashOpponents([]);
          setSquashTrainingDate('');
          setSquashSetNotice(error?.message || 'A mai ellenféllista nem tölthető be.');
        }
      } finally {
        if (!cancelled) {
          setSquashOpponentsLoading(false);
        }
      }
    };

    void loadOpponents();
    return () => {
      cancelled = true;
    };
  }, [currentUser, resultsMode, resultsPanelOpen]);

  useEffect(() => {
    if (!currentUser) {
      setPersonalRecords([]);
      return undefined;
    }

    const recordsQuery = query(
      collection(db, 'personal_records'),
      where('user_id', '==', currentUser.uid),
    );
    const unsubscribe = onSnapshot(
      recordsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((recordDoc) => {
          items.push(normalizePersonalRecord(recordDoc));
        });
        setPersonalRecords(items);
      },
      (error) => {
        setAuthNotice(`Személyes rekordok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setTrainingWeeks({});
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'training_weeks'),
      (snapshot) => {
        const weeks = {};
        snapshot.forEach((weekDoc) => {
          const data = weekDoc.data();
          const key = typeof data.training_date === 'string' ? data.training_date : weekDoc.id;
          weeks[key] = {
            cancelledByAdmin: data.cancelled_by_admin === true,
            participantCount: typeof data.count === 'number' && data.count >= 0 ? data.count : 0,
          };
        });
        setTrainingWeeks(weeks);
      },
      (error) => {
        setAuthNotice(`Edzés állapotok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setTrainingWeeks({});
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  const upcomingRegistrations = useMemo(() => {
    const now = new Date();
    const upcoming = [];

    registrations.forEach((registration) => {
      const startDate = getRegistrationStartDate(registration);
      if (startDate && startDate.getTime() < now.getTime()) {
        return;
      }
      upcoming.push(registration);
    });

    upcoming.sort(sortRegistrationsAscending);
    return upcoming;
  }, [registrations]);

  const handleCancelRegistration = async (registration) => {
    if (!currentUser || !registration?.trainingDate) {
      setCancelNotice('A lemondáshoz be kell jelentkezned.');
      return;
    }

    const trainingDate = registration.trainingDate;
    if (registration.lateCancelled) {
      setCancelNotice('Ezt az alkalmat már késői lemondásként rögzítettük.');
      return;
    }

    setCancelBusyDate(trainingDate);
    setCancelNotice('');

    try {
      const userEmail = (currentUser.email || '').trim().toLowerCase();
      const userDisplayName = (currentUser.displayName || '').trim();
      const registrationRef = doc(db, 'training_registrations', `${currentUser.uid}_${trainingDate}`);
      const lateCancellationRef = doc(db, 'late_cancellations', `${currentUser.uid}_${trainingDate}`);

      const registrationSnap = await getDoc(registrationRef);
      if (!registrationSnap.exists()) {
        throw new Error('Nincs jelentkezés erre az alkalomra.');
      }
      const registrationData = registrationSnap.data();

      if (!canCancelTraining(trainingDate)) {
        const batch = writeBatch(db);
        const lateCancelledAt = serverTimestamp();
        batch.update(registrationRef, {
          late_cancelled: true,
          late_cancelled_at: lateCancelledAt,
          updatedAt: serverTimestamp(),
        });
        batch.set(
          lateCancellationRef,
          {
            user_id: currentUser.uid,
            user_email: userEmail || registrationData.user_email || '',
            user_display_name: userDisplayName || registrationData.user_display_name || '',
            training_date: trainingDate,
            training_start_at: registrationData.training_start_at || getTrainingStartDateFromKey(trainingDate),
            registration_id: registrationRef.id,
            fee_percent: 70,
            cancelled_at: lateCancelledAt,
            createdAt: registrationData.late_cancelled_at || lateCancelledAt,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        await batch.commit();
        setLateCancelOpen(true);
        setCancelNotice(`Késői lemondás rögzítve: ${formatTrainingDate(trainingDate)}`);
        return;
      }

      await runTransaction(db, async (transaction) => {
        const liveRegistrationSnap = await transaction.get(registrationRef);

        if (!liveRegistrationSnap.exists()) {
          throw new Error('Nincs jelentkezés erre az alkalomra.');
        }

        transaction.delete(registrationRef);
        transaction.delete(lateCancellationRef);
      });

      setCancelNotice(`Sikeres lemondás: ${formatTrainingDate(trainingDate)}`);
    } catch (error) {
      if (error?.code === 'permission-denied') {
        setCancelNotice('Sikertelen lemondás: nincs jogosultság a művelethez (permission-denied).');
      } else {
        const codePart = error?.code ? ` (${error.code})` : '';
        setCancelNotice(`Sikertelen lemondás: ${error?.message || 'ismeretlen hiba'}${codePart}`);
      }
    } finally {
      setCancelBusyDate('');
    }
  };

  const exerciseById = useMemo(
    () => Object.fromEntries(exercises.map((exercise) => [exercise.id, exercise])),
    [exercises],
  );

  const selectableTrainingDates = useMemo(() => {
    const byDate = new Map();
    registrations.forEach((registration) => {
      if (registration.trainingDate) {
        byDate.set(registration.trainingDate, registration);
      }
    });
    return Array.from(byDate.values()).sort((left, right) => {
      const leftStart = getRegistrationStartDate(left);
      const rightStart = getRegistrationStartDate(right);
      if (leftStart && rightStart) {
        return rightStart.getTime() - leftStart.getTime();
      }
      return (right.trainingDate || '').localeCompare(left.trainingDate || '');
    });
  }, [registrations]);

  useEffect(() => {
    if (selectableTrainingDates.some((registration) => registration.trainingDate === selectedTaskTrainingDate)) {
      return;
    }
    setSelectedTaskTrainingDate(selectableTrainingDates[0]?.trainingDate || '');
  }, [selectableTrainingDates, selectedTaskTrainingDate]);

  useEffect(() => {
    if (!currentUser || !tasksPanelOpen || !selectedTaskTrainingDate) {
      setTrainingTaskAssignments([]);
      setTrainingTaskAssignmentsLoading(false);
      return undefined;
    }

    setTrainingTaskAssignmentsLoading(true);
    setTaskCompletionNotice('');
    const assignmentsRef = collection(
      db,
      'training_weeks',
      selectedTaskTrainingDate,
      'task_assignments',
    );
    const unsubscribe = onSnapshot(
      assignmentsRef,
      (snapshot) => {
        const items = [];
        snapshot.forEach((assignmentDoc) => {
          items.push(normalizeTrainingTaskAssignment(assignmentDoc));
        });
        items.sort((left, right) => {
          if (left.position !== right.position) {
            return left.position - right.position;
          }
          const createdAtDifference = (left.createdAt?.toMillis?.() || 0) - (right.createdAt?.toMillis?.() || 0);
          return createdAtDifference || left.id.localeCompare(right.id);
        });
        setTrainingTaskAssignments(items);
        setTrainingTaskAssignmentsLoading(false);
      },
      (error) => {
        setTaskCompletionNotice(`Feladatlista betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setTrainingTaskAssignments([]);
        setTrainingTaskAssignmentsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, selectedTaskTrainingDate, tasksPanelOpen]);

  useEffect(() => {
    if (!currentUser || !tasksPanelOpen || !selectedTaskTrainingDate) {
      setCompletedTrainingTaskIds(new Set());
      return undefined;
    }

    const completionRef = doc(
      db,
      'training_weeks',
      selectedTaskTrainingDate,
      'task_completion_states',
      currentUser.uid,
    );
    const unsubscribe = onSnapshot(
      completionRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        const completedIds = Array.isArray(data.completed_assignment_ids)
          ? data.completed_assignment_ids.filter((assignmentId) => typeof assignmentId === 'string')
          : [];
        setCompletedTrainingTaskIds(new Set(completedIds));
      },
      (error) => {
        setTaskCompletionNotice(`Teljesítések betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setCompletedTrainingTaskIds(new Set());
      },
    );

    return () => unsubscribe();
  }, [currentUser, selectedTaskTrainingDate, tasksPanelOpen]);

  const selectedExercise = exerciseById[resultForm.exerciseId] || null;
  const selectedLeaderboardExercise = exerciseById[leaderboardForm.exerciseId] || null;
  const selectedLeaderboardExerciseDirection = selectedLeaderboardExercise?.resultDirection || 'higher';
  const selectedLeaderboardTrainingDate = leaderboardForm.trainingDate || selectableTrainingDates[0]?.trainingDate || '';
  const selectedSquashOpponent = squashOpponents.find((opponent) => opponent.id === selectedSquashOpponentId) || null;
  const squashSetDraftsComplete = squashSetDrafts.length > 0 && squashSetDrafts.every((setDraft) => (
    (setDraft.winnerId === currentUser?.uid || setDraft.winnerId === selectedSquashOpponentId)
    && isValidSquashSetLoserScore(setDraft.loserScore)
  ));
  const squashHeadToHeadRows = useMemo(
    () => buildSquashHeadToHeadRows(squashSets, currentUser?.uid),
    [currentUser?.uid, squashSets],
  );

  useEffect(() => {
    if (!currentUser || resultForm.exerciseId || exercises.length === 0) {
      return;
    }

    setResultForm((current) => ({
      ...current,
      exerciseId: exercises[0].id,
    }));
  }, [currentUser, exercises, resultForm.exerciseId]);

  useEffect(() => {
    if (!currentUser || resultForm.trainingDate || selectableTrainingDates.length === 0) {
      return;
    }

    setResultForm((current) => ({
      ...current,
      trainingDate: selectableTrainingDates[0].trainingDate,
    }));
  }, [currentUser, resultForm.trainingDate, selectableTrainingDates]);

  useEffect(() => {
    if (!currentUser || leaderboardForm.exerciseId || exercises.length === 0) {
      return;
    }

    setLeaderboardForm((current) => ({
      ...current,
      exerciseId: exercises[0].id,
    }));
  }, [currentUser, exercises, leaderboardForm.exerciseId]);

  useEffect(() => {
    if (!currentUser || leaderboardForm.trainingDate || selectableTrainingDates.length === 0) {
      return;
    }

    setLeaderboardForm((current) => ({
      ...current,
      trainingDate: selectableTrainingDates[0].trainingDate,
    }));
  }, [currentUser, leaderboardForm.trainingDate, selectableTrainingDates]);

  useEffect(() => {
    if (!currentUser || !leaderboardForm.exerciseId) {
      setAllTimeRecords([]);
      return undefined;
    }

    const allTimeQuery = query(
      collection(db, 'personal_records'),
      where('exercise_id', '==', leaderboardForm.exerciseId),
      orderBy('sort_value', 'asc'),
      orderBy('achieved_at', 'asc'),
      firestoreLimit(50),
    );
    const unsubscribe = onSnapshot(
      allTimeQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((recordDoc) => {
          items.push(normalizePersonalRecord(recordDoc));
        });
        setAllTimeRecords(items);
      },
      (error) => {
        setAuthNotice(`All-time ranglista betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    return () => unsubscribe();
  }, [currentUser, leaderboardForm.exerciseId]);

  useEffect(() => {
    if (!currentUser || !leaderboardForm.exerciseId || !selectedLeaderboardTrainingDate) {
      setLeaderboardResults([]);
      return undefined;
    }

    const leaderboardQuery = query(
      collection(db, 'fitness_results'),
      where('exercise_id', '==', leaderboardForm.exerciseId),
      where('training_date', '==', selectedLeaderboardTrainingDate),
    );
    const unsubscribe = onSnapshot(
      leaderboardQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((resultDoc) => {
          items.push(normalizeFitnessResult(resultDoc));
        });
        setLeaderboardResults(items);
      },
      (error) => {
        setAuthNotice(`Edzés ranglista betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    return () => unsubscribe();
  }, [currentUser, leaderboardForm.exerciseId, selectedLeaderboardTrainingDate]);

  const groupedFitnessResults = useMemo(() => {
    const groups = new Map();
    fitnessResults.forEach((item) => {
      const exerciseId = item.exerciseId || 'unknown';
      if (!groups.has(exerciseId)) {
        groups.set(exerciseId, []);
      }
      groups.get(exerciseId).push(item);
    });

    return Array.from(groups.entries())
      .map(([exerciseId, items]) => ({
        exerciseId,
        exerciseName: getExerciseName(exerciseById, exerciseId, items[0]?.exerciseName),
        resultUnit: exerciseById[exerciseId]?.resultUnit || items[0]?.resultUnit || '',
        items: [...items].sort((left, right) =>
          getTimestampMillis(left.recordedAt || left.createdAt) - getTimestampMillis(right.recordedAt || right.createdAt),
        ),
      }))
      .sort((left, right) => left.exerciseName.localeCompare(right.exerciseName, 'hu'));
  }, [exerciseById, fitnessResults]);

  const sortedPersonalRecords = useMemo(() => {
    return sortRankingRows(personalRecords, 'lower')
      .sort((left, right) =>
        getExerciseName(exerciseById, left.exerciseId, left.exerciseName)
          .localeCompare(getExerciseName(exerciseById, right.exerciseId, right.exerciseName), 'hu'),
      );
  }, [exerciseById, personalRecords]);

  const trainingLeaderboardRows = useMemo(() => {
    const bestRows = buildBestResultsByUser(leaderboardResults, selectedLeaderboardExerciseDirection);
    return sortRankingRows(bestRows, selectedLeaderboardExerciseDirection);
  }, [leaderboardResults, selectedLeaderboardExerciseDirection]);

  const hasAnyBillingData = hasAnyAddressData(billingForm);

  const handleGoogleLogin = async () => {
    setAuthBusy(true);
    setAuthNotice('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await upsertUserInFirestore(db, result.user);
    } catch (error) {
      setAuthNotice(getFriendlyAuthErrorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setAuthBusy(true);
    setAuthNotice('');

    try {
      await signInWithVerifiedEmailPassword({
        auth,
        cloudFunctions,
        db,
        email: authEmail,
        password: authPassword,
      });
    } catch (error) {
      setAuthNotice(getFriendlyAuthErrorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handlePasswordReset = async () => {
    setAuthBusy(true);
    setAuthNotice('');

    try {
      const resetResult = await sendPasswordReset({ cloudFunctions, email: authEmail });
      if (
        resetResult.status === 'google_provider'
        || resetResult.status === 'provider_without_password'
      ) {
        setPasswordManagedByGoogleNoticeOpen(true);
        return;
      }

      setAuthNotice('Ha ehhez az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító levelet.');
    } catch (error) {
      if (error?.code === 'auth/user-not-found') {
        setAuthNotice('Ha ehhez az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító levelet.');
      } else {
        setAuthNotice(getFriendlyAuthErrorMessage(error));
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setAuthNotice('');
    } catch (error) {
      setAuthNotice(`Kijelentkezési hiba: ${error?.message || 'ismeretlen hiba'}`);
    }
  };

  const loadBillingAddress = async () => {
    if (!currentUser) {
      return;
    }

    if (billingLoaded || billingLoading) {
      return;
    }

    setBillingLoading(true);
    setBillingSaveNotice('');

    try {
      const [userSnapshot, profileSnapshot] = await Promise.all([
        getDoc(doc(db, 'users', currentUser.uid)),
        getDoc(doc(db, 'profiles', currentUser.uid)),
      ]);

      const merged = {
        ...(profileSnapshot.exists() ? profileSnapshot.data() : {}),
        ...(userSnapshot.exists() ? userSnapshot.data() : {}),
      };
      const address = getUserAddress(merged);
      setBillingForm({
        name: getUserBillingName(merged) || currentUser.displayName || '',
        country: address.country || DEFAULT_BILLING_COUNTRY,
        zip: address.zip || '',
        city: address.city || getCityByHungarianPostalCode(address.zip),
        street: address.street || '',
      });
      setBillingLoaded(true);
    } catch (error) {
      setBillingSaveNotice(`Számlázási adatok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleOpenBillingPanel = () => {
    setBillingPanelOpen(true);
    setResultsPanelOpen(false);
    setTasksPanelOpen(false);
    setNicknamePanelOpen(false);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(false);
    void loadBillingAddress();
  };

  const handleOpenTrainingPanel = () => {
    setBillingPanelOpen(false);
    setResultsPanelOpen(false);
    setTasksPanelOpen(false);
    setNicknamePanelOpen(false);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(false);
  };

  const handleOpenResultsPanel = () => {
    setBillingPanelOpen(false);
    setResultsPanelOpen(true);
    setTasksPanelOpen(false);
    setNicknamePanelOpen(false);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(false);
  };

  const handleOpenNicknamePanel = () => {
    setBillingPanelOpen(false);
    setResultsPanelOpen(false);
    setTasksPanelOpen(false);
    setNicknamePanelOpen(true);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(false);
  };

  const handleOpenTasksPanel = () => {
    setBillingPanelOpen(false);
    setResultsPanelOpen(false);
    setTasksPanelOpen(true);
    setNicknamePanelOpen(false);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(false);
  };

  const handleOpenNotificationPanel = () => {
    setBillingPanelOpen(false);
    setResultsPanelOpen(false);
    setTasksPanelOpen(false);
    setNicknamePanelOpen(false);
    setNotificationPanelOpen(true);
    setProfileManagementPanelOpen(false);
  };

  const handleOpenProfileManagementPanel = () => {
    setBillingPanelOpen(false);
    setResultsPanelOpen(false);
    setTasksPanelOpen(false);
    setNicknamePanelOpen(false);
    setNotificationPanelOpen(false);
    setProfileManagementPanelOpen(true);
  };

  const handleToggleTrainingTaskCompletion = async (assignmentId) => {
    if (
      !currentUser
      || !assignmentId
      || !selectableTrainingDates.some((registration) => registration.trainingDate === selectedTaskTrainingDate)
    ) {
      setTaskCompletionNotice('Ehhez az edzéshez nincs érvényes jelentkezésed.');
      return;
    }

    const isCompleted = completedTrainingTaskIds.has(assignmentId);
    setTaskCompletionBusyId(assignmentId);
    setTaskCompletionNotice('');
    try {
      await setDoc(
        doc(
          db,
          'training_weeks',
          selectedTaskTrainingDate,
          'task_completion_states',
          currentUser.uid,
        ),
        {
          user_id: currentUser.uid,
          training_date: selectedTaskTrainingDate,
          completed_assignment_ids: isCompleted ? arrayRemove(assignmentId) : arrayUnion(assignmentId),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      setTaskCompletionNotice(`Teljesítés mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTaskCompletionBusyId('');
    }
  };

  const handleResultFormChange = (field, value) => {
    setResultForm((current) => ({
      ...current,
      [field]: value,
    }));
    setResultNotice('');
  };

  const handleResultsModeChange = (mode) => {
    setResultsMode(mode);
    setResultNotice('');
    setSquashSetNotice('');
  };

  const handleSelectSquashOpponent = (opponentId) => {
    setSelectedSquashOpponentId(opponentId);
    setSquashSetNotice('');
  };

  const handleConfirmSquashOpponent = () => {
    if (!selectedSquashOpponent) {
      setSquashSetNotice('Válassz ellenfelet a folytatáshoz.');
      return;
    }
    setSquashOpponentConfirmed(true);
    setSquashSetDrafts([createSquashSetDraft()]);
    setSquashSetNotice('');
  };

  const handleChangeSquashOpponent = () => {
    setSquashOpponentConfirmed(false);
    setSelectedSquashOpponentId('');
    setSquashSetDrafts([createSquashSetDraft()]);
    setSquashSetNotice('');
  };

  const handleSquashSetDraftChange = (draftId, field, value) => {
    setSquashSetDrafts((current) => current.map((draft) => (
      draft.id === draftId ? { ...draft, [field]: value } : draft
    )));
    setSquashSetNotice('');
  };

  const handleAddSquashSetDraft = () => {
    if (!squashSetDraftsComplete) {
      setSquashSetNotice('Előbb töltsd ki az aktuális szett eredményét.');
      return;
    }
    setSquashSetDrafts((current) => [...current, createSquashSetDraft()]);
    setSquashSetNotice('');
  };

  const handleRemoveSquashSetDraft = (draftId) => {
    setSquashSetDrafts((current) => current.filter((draft) => draft.id !== draftId));
    setSquashSetNotice('');
  };

  const handleSaveSquashSets = async () => {
    if (!selectedSquashOpponent || !squashSetDraftsComplete) {
      setSquashSetNotice('Minden szetthez válassz győztest és add meg a vesztes pontszámát.');
      return;
    }

    setSquashSetSaveBusy(true);
    setSquashSetNotice('');
    try {
      const recordSets = httpsCallable(cloudFunctions, 'recordSquashSets');
      await recordSets({
        opponentId: selectedSquashOpponent.id,
        sets: squashSetDrafts.map((draft) => ({
          winnerId: draft.winnerId,
          loserScore: Number(draft.loserScore),
        })),
      });
      setSquashOpponentConfirmed(false);
      setSelectedSquashOpponentId('');
      setSquashSetDrafts([createSquashSetDraft()]);
      setSquashSetNotice('Szett eredmények mentve.');
    } catch (error) {
      setSquashSetNotice(error?.message || 'A szett eredmények mentése nem sikerült.');
    } finally {
      setSquashSetSaveBusy(false);
    }
  };

  const handleStartSquashSetEdit = (set) => {
    setSquashSetEditDrafts((current) => ({
      ...current,
      [set.id]: {
        winnerId: set.winnerId,
        loserScore: String(getSquashSetLoserScore(set)),
      },
    }));
    setSquashSetNotice('');
  };

  const handleSquashSetEditChange = (setId, field, value) => {
    setSquashSetEditDrafts((current) => ({
      ...current,
      [setId]: {
        ...(current[setId] || {}),
        [field]: value,
      },
    }));
    setSquashSetNotice('');
  };

  const handleCancelSquashSetEdit = (setId) => {
    setSquashSetEditDrafts((current) => {
      const next = { ...current };
      delete next[setId];
      return next;
    });
  };

  const handleSaveSquashSetEdit = async (set) => {
    const draft = squashSetEditDrafts[set.id];
    if (
      !draft
      || (draft.winnerId !== set.playerAId && draft.winnerId !== set.playerBId)
      || !isValidSquashSetLoserScore(draft.loserScore)
    ) {
      setSquashSetNotice('Válassz győztest és adj meg érvényes vesztes pontszámot.');
      return;
    }

    setSquashSetBusyId(set.id);
    setSquashSetNotice('');
    try {
      const updateSet = httpsCallable(cloudFunctions, 'updateSquashSet');
      await updateSet({
        setId: set.id,
        winnerId: draft.winnerId,
        loserScore: Number(draft.loserScore),
      });
      handleCancelSquashSetEdit(set.id);
      setSquashSetNotice('Szett eredménye frissítve.');
    } catch (error) {
      setSquashSetNotice(error?.message || 'A szett eredményének módosítása nem sikerült.');
    } finally {
      setSquashSetBusyId('');
    }
  };

  const handleDeleteSquashSet = async (set) => {
    if (!window.confirm('Biztosan törlöd ezt a szett eredményt?')) {
      return;
    }

    setSquashSetBusyId(set.id);
    setSquashSetNotice('');
    try {
      const deleteSet = httpsCallable(cloudFunctions, 'deleteSquashSet');
      await deleteSet({ setId: set.id });
      setSquashSetNotice('Szett eredménye törölve.');
    } catch (error) {
      setSquashSetNotice(error?.message || 'A szett eredményének törlése nem sikerült.');
    } finally {
      setSquashSetBusyId('');
    }
  };

  const handleLeaderboardFormChange = (field, value) => {
    setLeaderboardForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSaveFitnessResult = async () => {
    if (!currentUser) {
      setResultNotice('A mentéshez be kell jelentkezned.');
      return;
    }

    const exercise = exerciseById[resultForm.exerciseId];
    const trainingDate = resultForm.trainingDate;
    const resultValue = Number.parseFloat(String(resultForm.value).replace(',', '.'));

    if (!exercise) {
      setResultNotice('Válassz gyakorlatot.');
      return;
    }
    if (!trainingDate) {
      setResultNotice('Válassz edzést.');
      return;
    }
    if (!Number.isFinite(resultValue)) {
      setResultNotice('Adj meg érvényes eredményt.');
      return;
    }

    setResultSaveBusy(true);
    setResultNotice('');

    try {
      await addDoc(collection(db, 'fitness_results'), {
        user_id: currentUser.uid,
        user_email: currentUser.email || '',
        user_display_name: currentUser.displayName || '',
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        result_unit: exercise.resultUnit,
        result_direction: exercise.resultDirection,
        training_date: trainingDate,
        training_id: trainingDate,
        result_value: resultValue,
        sort_value: getResultSortValue(resultValue, exercise.resultDirection),
        recorded_at: serverTimestamp(),
        recorded_by_user_id: currentUser.uid,
        source: 'user',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setResultForm((current) => ({ ...current, value: '' }));
      setResultNotice('Eredmény mentve.');
    } catch (error) {
      setResultNotice(`Eredmény mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setResultSaveBusy(false);
    }
  };

  const handleBillingFieldChange = (field, value) => {
    setBillingForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'zip' ? { city: getCityByHungarianPostalCode(value) || current.city } : {}),
    }));
    setBillingSaveNotice('');
  };

  const handleNicknameChange = (value) => {
    setNicknameForm(formatNickname(value));
    setNicknameSaveNotice('');
  };

  const handleEmailPreferenceChange = (key, checked) => {
    setEmailPreferences((current) => ({
      ...current,
      [key]: checked,
    }));
    setEmailPreferencesNotice('');
  };

  const handleGenerateNickname = () => {
    const nextNickname = getRandomNickname(nicknameForm, reservedNicknameKeys);
    if (!nextNickname) {
      setNicknameSaveNotice('Nincs szabad becenév a generátor listájában.');
      return;
    }
    setNicknameForm(nextNickname);
    setNicknameSaveNotice('');
  };

  const handleSaveNickname = async () => {
    if (!currentUser) {
      setNicknameSaveNotice('A mentéshez be kell jelentkezned.');
      return;
    }

    setNicknameSaveBusy(true);
    setNicknameSaveNotice('');

    try {
      const nextNickname = formatNickname(nicknameForm);
      const nextNicknameKey = getNicknameKey(nextNickname);

      if (!nextNickname) {
        setNicknameSaveNotice('Adj meg becenevet, vagy generálj egyet.');
        setNicknameSaveBusy(false);
        return;
      }

      if (!nextNicknameKey) {
        setNicknameSaveNotice('A becenév tartalmazzon legalább egy betűt vagy számot.');
        setNicknameSaveBusy(false);
        return;
      }

      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnapshot = await transaction.get(userRef);
        const currentData = userSnapshot.exists() ? userSnapshot.data() : {};
        const currentNicknameKey = getNicknameKey(getTextField(currentData.nickname, currentData.nickName, currentData.becenev));
        const nicknameRef = doc(db, 'nicknames', nextNicknameKey);
        const nicknameSnapshot = await transaction.get(nicknameRef);
        const currentNicknameRef =
          currentNicknameKey && currentNicknameKey !== nextNicknameKey
            ? doc(db, 'nicknames', currentNicknameKey)
            : null;
        const currentNicknameSnapshot = currentNicknameRef ? await transaction.get(currentNicknameRef) : null;

        if (nicknameSnapshot.exists() && nicknameSnapshot.data()?.user_id !== currentUser.uid) {
          throw new Error('Ez a becenév már foglalt. Válassz vagy generálj másikat.');
        }

        transaction.set(
          userRef,
          {
            nickname: nextNickname,
            nickname_key: nextNicknameKey,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        transaction.set(nicknameRef, {
          nickname: nextNickname,
          user_id: currentUser.uid,
          updatedAt: serverTimestamp(),
          createdAt: nicknameSnapshot.exists() ? nicknameSnapshot.data()?.createdAt || serverTimestamp() : serverTimestamp(),
        });

        if (currentNicknameRef && currentNicknameSnapshot?.exists() && currentNicknameSnapshot.data()?.user_id === currentUser.uid) {
          transaction.delete(currentNicknameRef);
        }
      });
      setNicknameForm(nextNickname);
      setNicknameSaveNotice('Sikeres mentés.');
    } catch (error) {
      setNicknameSaveNotice(`Mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setNicknameSaveBusy(false);
    }
  };

  const handleSaveBillingAddress = async () => {
    if (!currentUser) {
      setBillingSaveNotice('A mentéshez be kell jelentkezned.');
      return;
    }

    setBillingSaveBusy(true);
    setBillingSaveNotice('');

    try {
      if (!hasCompleteBillingData({
        ...billingForm,
        name: billingForm.name.trim(),
        country: billingForm.country.trim(),
        zip: billingForm.zip.trim(),
        city: billingForm.city.trim(),
        street: billingForm.street.trim(),
      })) {
        setBillingSaveNotice('A számlázáshoz a név és a teljes cím megadása kötelező.');
        setBillingSaveBusy(false);
        return;
      }

      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          billing_name: billingForm.name.trim(),
          billing_country: billingForm.country.trim(),
          billing_zip: billingForm.zip.trim(),
          billing_city: billingForm.city.trim(),
          billing_address: billingForm.street.trim(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setBillingLoaded(true);
      setBillingSaveNotice('Sikeres mentés.');
    } catch (error) {
      setBillingSaveNotice(`Mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBillingSaveBusy(false);
    }
  };

  const handleSaveEmailPreferences = async () => {
    if (!currentUser) {
      setEmailPreferencesNotice('A mentéshez be kell jelentkezned.');
      return;
    }

    setEmailPreferencesSaveBusy(true);
    setEmailPreferencesNotice('');

    try {
      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          email_preferences: pickEmailPreferences({ email_preferences: emailPreferences }),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setEmailPreferencesNotice('Sikeres mentés.');
    } catch (error) {
      setEmailPreferencesNotice(`Mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setEmailPreferencesSaveBusy(false);
    }
  };

  const handlePasswordChangeFormChange = (field, value) => {
    setPasswordChangeForm((current) => ({
      ...current,
      [field]: value,
    }));
    setPasswordChangeNotice('');
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (!currentUser) {
      setPasswordChangeNotice('A jelszó módosításához be kell jelentkezned.');
      return;
    }

    if (!isPasswordUser(currentUser)) {
      setPasswordChangeNotice('A jelszavadat a Google fiókodban tudod megváltoztatni.');
      return;
    }

    const currentPassword = passwordChangeForm.currentPassword;
    const nextPassword = passwordChangeForm.nextPassword;
    const nextPasswordConfirm = passwordChangeForm.nextPasswordConfirm;

    if (!currentPassword || !nextPassword || !nextPasswordConfirm) {
      setPasswordChangeNotice('Tölts ki minden jelszómezőt.');
      return;
    }

    if (nextPassword !== nextPasswordConfirm) {
      setPasswordChangeNotice('Az új jelszó és az ismétlés nem egyezik.');
      return;
    }

    if (currentPassword === nextPassword) {
      setPasswordChangeNotice('Az új jelszó nem lehet azonos a jelenlegi jelszóval.');
      return;
    }

    const passwordValidationMessage = getPasswordValidationMessage(nextPassword);
    if (passwordValidationMessage) {
      setPasswordChangeNotice(passwordValidationMessage);
      return;
    }

    if (!currentUser.email) {
      setPasswordChangeNotice('Ehhez a fiókhoz nem tartozik jelszóval kezelhető e-mail cím.');
      return;
    }

    setPasswordChangeBusy(true);
    setPasswordChangeNotice('');

    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, nextPassword);
      setPasswordChangeForm(initialPasswordChangeForm);
      setPasswordChangeNotice('Sikeres jelszómódosítás.');
    } catch (error) {
      if (error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password') {
        setPasswordChangeNotice('A jelenlegi jelszó nem megfelelő.');
      } else if (error?.code === 'auth/requires-recent-login') {
        setPasswordChangeNotice('A jelszó módosításához jelentkezz be újra, majd próbáld meg ismét.');
      } else {
        setPasswordChangeNotice(getFriendlyAuthErrorMessage(error));
      }
    } finally {
      setPasswordChangeBusy(false);
    }
  };

  const handleDeleteOwnProfile = async () => {
    if (!currentUser) {
      setAccountDeleteNotice('A törléshez be kell jelentkezned.');
      return;
    }

    setAccountDeleteBusy(true);
    setAccountDeleteNotice('');

    try {
      const deleteUserAccount = httpsCallable(cloudFunctions, 'deleteUserAccount');
      await deleteUserAccount({ userId: currentUser.uid });
      setAccountDeleteConfirmOpen(false);
      await signOut(auth);
      window.location.href = '/';
    } catch (error) {
      setAccountDeleteNotice(`Profil törlési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setAccountDeleteBusy(false);
    }
  };

  return (
    <div className="admin-shell profile-shell">
      <header className="admin-topbar profile-topbar">
        <button className="brand" onClick={() => (window.location.href = '/?home=1')}>
          PNG<span>SQUASH</span>
        </button>
        <button
          type="button"
          className="button-soft profile-menu-toggle"
          onClick={() => setProfileActionsOpen((isOpen) => !isOpen)}
          aria-label={profileActionsOpen ? 'Profil menü bezárása' : 'Profil menü megnyitása'}
          aria-expanded={profileActionsOpen}
        >
          {profileActionsOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        <div className="admin-topbar-center">
          {currentUser && (
            <div className="profile-center-controls">
              <div className="profile-view-switch" role="group" aria-label="Profil nézet választó">
                <button
                  type="button"
                  className={`profile-view-tab ${!billingPanelOpen && !resultsPanelOpen && !tasksPanelOpen && !nicknamePanelOpen && !notificationPanelOpen && !profileManagementPanelOpen ? 'active' : ''}`}
                  onClick={handleOpenTrainingPanel}
                >
                  Edzés alkalmak
                </button>
                <button
                  type="button"
                  className={`profile-view-tab ${nicknamePanelOpen ? 'active' : ''}`}
                  onClick={handleOpenNicknamePanel}
                >
                  Becenév
                </button>
                <button
                  type="button"
                  className={`profile-view-tab ${tasksPanelOpen ? 'active' : ''}`}
                  onClick={handleOpenTasksPanel}
                >
                  Feladatok
                </button>
                <button
                  type="button"
                  className={`profile-view-tab ${resultsPanelOpen ? 'active' : ''}`}
                  onClick={handleOpenResultsPanel}
                >
                  Eredmények
                </button>
                <button
                  type="button"
                  className={`profile-view-tab ${billingPanelOpen ? 'active' : ''}`}
                  onClick={handleOpenBillingPanel}
                >
                  Számlázási adatok
                </button>
                <button
                  type="button"
                  className={`profile-view-tab ${notificationPanelOpen ? 'active' : ''}`}
                  onClick={handleOpenNotificationPanel}
                >
                  Értesítések
                </button>
                <button
                  type="button"
                  className={`profile-view-tab ${profileManagementPanelOpen ? 'active' : ''}`}
                  onClick={handleOpenProfileManagementPanel}
                >
                  Profil kezelés
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={`admin-topbar-actions profile-actions-menu ${profileActionsOpen ? 'open' : ''}`}>
          <button className="button-soft profile-home-button" onClick={() => (window.location.href = '/?home=1')}>
            <Home size={16} />
            Vissza a főoldalra
          </button>
          {currentUser ? (
            <>
              <span className="admin-user">{currentUser.displayName || currentUser.email}</span>
              <button className="button-soft" onClick={handleLogout}>
                <LogOut size={16} />
                Kijelentkezés
              </button>
            </>
          ) : (
            <button className="button-primary" onClick={handleGoogleLogin} disabled={authBusy}>
              <LogIn size={16} />
              {authBusy ? 'Belépés...' : 'Belépés Google-lel'}
            </button>
          )}
        </div>
      </header>

      <main className="admin-main container profile-main">
        {!currentUser && (
          <section className="card card-large admin-warning">
            <h2>Belépés szükséges</h2>
            <p>A profil megtekintéséhez jelentkezz be Google fiókkal vagy e-mail címmel.</p>
            <div className="profile-auth-actions">
              <button className="button-primary" onClick={handleGoogleLogin} disabled={authBusy}>
                <LogIn size={16} />
                {authBusy ? 'Belépés...' : 'Belépés Google-lel'}
              </button>
              <form className="auth-form profile-auth-form" onSubmit={handleEmailLogin}>
                <label className="auth-field">
                  <span>E-mail cím</span>
                  <input
                    className="auth-input"
                    type="email"
                    value={authEmail}
                    onChange={(event) => setAuthEmail(event.target.value)}
                    autoComplete="email"
                    required
                  />
                </label>
                <label className="auth-field">
                  <span>Jelszó</span>
                  <input
                    className="auth-input"
                    type="password"
                    value={authPassword}
                    onChange={(event) => setAuthPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>
                <button className="button-primary" type="submit" disabled={authBusy}>
                  <LogIn size={16} />
                  Belépés e-maillel
                </button>
                <button className="button-soft" type="button" onClick={handlePasswordReset} disabled={authBusy}>
                  Elfelejtett jelszó
                </button>
              </form>
            </div>
            {authNotice && <p className="registration-notice error">{authNotice}</p>}
          </section>
        )}

        {currentUser && (
          <>
            {authNotice && <p className="registration-notice error">{authNotice}</p>}
            {!billingPanelOpen && !resultsPanelOpen && !tasksPanelOpen && !nicknamePanelOpen && !notificationPanelOpen && !profileManagementPanelOpen && (
              <section className="card card-large profile-card profile-card-wide">
                <div className="profile-section-head">
                  <h2>Edzés alkalmak</h2>
                </div>

                {cancelNotice && (
                  <p className={`registration-notice ${cancelNotice.startsWith('Sikeres') || cancelNotice.startsWith('Késői') ? 'ok' : 'error'}`}>
                    {cancelNotice}
                  </p>
                )}

                {registrationsLoading ? (
                  <p className="profile-muted">Jelentkezések betöltése...</p>
                ) : upcomingRegistrations.length === 0 ? (
                  <p className="profile-muted">Nincs aktív jelentkezésed.</p>
                ) : (
                  <ul className="profile-training-list">
                    {upcomingRegistrations.map((item) => {
                      const isCancelledByAdmin = trainingWeeks[item.trainingDate]?.cancelledByAdmin === true;
                      const isCancelBusy = cancelBusyDate === item.trainingDate;
                      const canCancel = !isCancelledByAdmin && !item.lateCancelled;
                      return (
                        <li
                          key={item.id}
                          className={`profile-training-item ${isCancelledByAdmin ? 'cancelled' : ''}`}
                        >
                          <div className="profile-training-main">
                            <div className="profile-training-badges">
                              {isCancelledByAdmin && (
                                <div className="profile-training-cancelled">Lemondva a szervező által</div>
                              )}
                              {item.lateCancelled && (
                                <div className="profile-training-cancelled">Késői lemondás rögzítve</div>
                              )}
                            </div>
                            <div className="profile-training-date">{formatTrainingDate(item.trainingDate)}</div>
                            <div className="profile-training-time">{formatTrainingTimeRange(item)}</div>
                          </div>

                          <div className="profile-training-details">
                            <div className="profile-training-meta">
                              Jelentkezők:{' '}
                              <strong>{trainingWeeks[item.trainingDate]?.participantCount ?? 0} fő</strong>
                            </div>
                            <div className="profile-training-meta">
                              Jelentkezés ideje: {formatRegistrationTimestamp(item.createdAt)}
                            </div>
                            {item.lateCancelled && (
                              <div className="profile-training-meta">
                                Lemondás ideje: {formatRegistrationTimestamp(item.lateCancelledAt)}
                              </div>
                            )}
                          </div>

                          <div className="profile-training-actions">
                            <button
                              type="button"
                              className="button-soft profile-training-cancel-button"
                              onClick={() => handleCancelRegistration(item)}
                              disabled={!canCancel || isCancelBusy}
                            >
                              {isCancelBusy ? 'Lemondás...' : item.lateCancelled ? 'Lemondva' : 'Lemondás'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="profile-training-register-action">
                  <a className="button-primary profile-training-register-link" href="/?home=1#register">
                    <Plus size={17} aria-hidden="true" />
                    Jelentkezés edzésre
                  </a>
                </div>
              </section>
            )}

            {nicknamePanelOpen && (
              <section className="card card-large profile-card profile-card-wide">
                <div className="profile-section-head">
                  <h2>Becenév</h2>
                </div>

                {nicknameLoading ? (
                  <p className="profile-muted">Becenév betöltése...</p>
                ) : (
                  <>
                    <form
                      className="profile-nickname-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleSaveNickname();
                      }}
                    >
                      <label className="admin-field profile-nickname-field">
                        <span>Becenév</span>
                        <input
                          type="text"
                          value={nicknameForm}
                          onChange={(event) => handleNicknameChange(event.target.value)}
                          placeholder="pl. Fiatal Titán"
                          maxLength={48}
                        />
                      </label>
                      <div className="profile-nickname-actions">
                        <button type="button" className="button-soft" onClick={handleGenerateNickname}>
                          <WandSparkles size={16} />
                          Becenév generálása
                        </button>
                        <button type="submit" className="button-primary" disabled={nicknameSaveBusy}>
                          {nicknameSaveBusy ? 'Mentés...' : 'Becenév mentése'}
                        </button>
                      </div>
                    </form>

                    {nicknameForm && (
                      <p className="profile-nickname-preview">
                        <strong>{nicknameForm}</strong>
                      </p>
                    )}

                    {nicknameSaveNotice && (
                      <p className={`registration-notice ${nicknameSaveNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                        {nicknameSaveNotice}
                      </p>
                    )}
                  </>
                )}
              </section>
            )}

            {tasksPanelOpen && (
              <section className="card card-large profile-card profile-card-wide profile-tasks-card">
                <div className="profile-section-head">
                  <h2>Feladatok</h2>
                  {trainingTaskAssignments.length > 0 && (
                    <strong className="profile-task-progress">
                      {trainingTaskAssignments.filter((assignment) => completedTrainingTaskIds.has(assignment.id)).length}
                      /{trainingTaskAssignments.length} kész
                    </strong>
                  )}
                </div>

                <label className="admin-field profile-task-training-select">
                  <span>Edzés</span>
                  <select
                    value={selectedTaskTrainingDate}
                    onChange={(event) => {
                      setSelectedTaskTrainingDate(event.target.value);
                      setTaskCompletionNotice('');
                    }}
                    disabled={registrationsLoading || selectableTrainingDates.length === 0}
                  >
                    {selectableTrainingDates.length === 0 ? (
                      <option value="">Nincs választható edzés</option>
                    ) : (
                      selectableTrainingDates.map((registration) => (
                        <option key={registration.id} value={registration.trainingDate}>
                          {formatTrainingDate(registration.trainingDate)} - {formatTrainingTimeRange(registration)}
                          {registration.lateCancelled ? ' (késői lemondás)' : ''}
                        </option>
                      ))
                    )}
                  </select>
                </label>

                {taskCompletionNotice && (
                  <p className="registration-notice error">{taskCompletionNotice}</p>
                )}

                {registrationsLoading ? (
                  <p className="profile-muted">Edzések betöltése...</p>
                ) : selectableTrainingDates.length === 0 ? (
                  <p className="profile-muted">Még nincs olyan edzésed, amelyhez feladatlista tartozhat.</p>
                ) : trainingTaskAssignmentsLoading ? (
                  <p className="profile-muted">Feladatlista betöltése...</p>
                ) : trainingTaskAssignments.length === 0 ? (
                  <p className="profile-muted">Ehhez az edzéshez még nincs feladat hozzárendelve.</p>
                ) : (
                  <ol className="profile-task-list">
                    {trainingTaskAssignments.map((assignment, index) => {
                      const isCompleted = completedTrainingTaskIds.has(assignment.id);
                      return (
                        <li key={assignment.id} className={isCompleted ? 'completed' : ''}>
                          <label className="profile-task-check">
                            <input
                              type="checkbox"
                              checked={isCompleted}
                              onChange={() => handleToggleTrainingTaskCompletion(assignment.id)}
                              disabled={Boolean(taskCompletionBusyId)}
                              aria-label={`${assignment.taskName} teljesítve`}
                            />
                          </label>
                          <span className="profile-task-number">{index + 1}</span>
                          <div className="profile-task-copy">
                            <strong>{assignment.taskName}</strong>
                            <span className="profile-task-quantity">
                              <b>{assignment.quantity}</b> {assignment.unit}
                            </span>
                            <small>{assignment.taskCategory || 'Kategória nélkül'}</small>
                            {assignment.description && <p>{assignment.description}</p>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>
            )}

            {resultsPanelOpen && (
              <section className="card card-large profile-card profile-card-wide profile-results-card">
                <div className="profile-section-head">
                  <h2>Eredmények</h2>
                  <div className="results-mode-switch" role="group" aria-label="Eredménytípus választó">
                    <button
                      type="button"
                      className={resultsMode === 'exercises' ? 'active' : ''}
                      onClick={() => handleResultsModeChange('exercises')}
                    >
                      Gyakorlatok
                    </button>
                    <button
                      type="button"
                      className={resultsMode === 'sets' ? 'active' : ''}
                      onClick={() => handleResultsModeChange('sets')}
                    >
                      Szettek
                    </button>
                  </div>
                </div>

                {resultsMode === 'exercises' ? (
                  <>
                <div className="results-layout">
                  <form
                    className="results-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSaveFitnessResult();
                    }}
                  >
                    <label className="admin-field">
                      <span>Gyakorlat</span>
                      <select
                        value={resultForm.exerciseId}
                        onChange={(event) => handleResultFormChange('exerciseId', event.target.value)}
                        disabled={exercisesLoading || exercises.length === 0}
                      >
                        {exercises.length === 0 ? (
                          <option value="">Nincs elérhető gyakorlat</option>
                        ) : (
                          exercises.map((exercise) => (
                            <option key={exercise.id} value={exercise.id}>
                              {formatExerciseTask(exercise) ? `${exercise.name} (${formatExerciseTask(exercise)})` : exercise.name}
                            </option>
                          ))
                        )}
                      </select>
                    </label>
                    <label className="admin-field">
                      <span>Edzés</span>
                      <select
                        value={resultForm.trainingDate}
                        onChange={(event) => handleResultFormChange('trainingDate', event.target.value)}
                        disabled={selectableTrainingDates.length === 0}
                      >
                        {selectableTrainingDates.length === 0 ? (
                          <option value="">Nincs edzés jelentkezés</option>
                        ) : (
                          selectableTrainingDates.map((registration) => (
                            <option key={registration.id} value={registration.trainingDate}>
                              {formatTrainingDate(registration.trainingDate)}
                            </option>
                          ))
                        )}
                      </select>
                    </label>
                    <label className="admin-field">
                      <span>Eredmény {selectedExercise?.resultUnit ? `(${selectedExercise.resultUnit})` : ''}</span>
                      <input
                        type="number"
                        step="0.01"
                        value={resultForm.value}
                        onChange={(event) => handleResultFormChange('value', event.target.value)}
                        placeholder="pl. 4.52"
                      />
                    </label>
                    {selectedExercise && formatExerciseTask(selectedExercise) && (
                      <p className="profile-muted">Feladat: {formatExerciseTask(selectedExercise)}</p>
                    )}
                    <button
                      type="submit"
                      className="button-primary"
                      disabled={resultSaveBusy || exercises.length === 0 || selectableTrainingDates.length === 0}
                    >
                      {resultSaveBusy ? 'Mentés...' : 'Eredmény mentése'}
                    </button>
                    {resultNotice && (
                      <p className={`registration-notice ${resultNotice.startsWith('Eredmény mentve') ? 'ok' : 'error'}`}>
                        {resultNotice}
                      </p>
                    )}
                  </form>

                  <div className="personal-records-panel">
                    <h3>Személyes rekordok</h3>
                    {sortedPersonalRecords.length === 0 ? (
                      <p className="profile-muted">Még nincs személyes rekordod.</p>
                    ) : (
                      <ul className="record-list">
                        {sortedPersonalRecords.map((record) => (
                          <li key={record.id}>
                            <span>{getExerciseName(exerciseById, record.exerciseId, record.exerciseName)}</span>
                            <strong>{formatResultValue(record.resultValue, record.resultUnit)}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="results-grid">
                  <section className="results-panel">
                    <h3>Saját korábbi eredmények</h3>
                    {fitnessResultsLoading ? (
                      <p className="profile-muted">Eredmények betöltése...</p>
                    ) : groupedFitnessResults.length === 0 ? (
                      <p className="profile-muted">Még nincs rögzített eredményed.</p>
                    ) : (
                      <div className="result-groups">
                        {groupedFitnessResults.map((group) => {
                          const groupExercise = exerciseById[group.exerciseId];
                          const groupExerciseTask = formatExerciseTask(groupExercise);
                          const values = group.items.map((item) => Number(item.resultValue)).filter(Number.isFinite);
                          const maxValue = Math.max(...values);
                          const minValue = Math.min(...values);
                          const span = Math.max(maxValue - minValue, 1);

                          return (
                            <article className="result-group" key={group.exerciseId}>
                              <h4>{groupExerciseTask ? `${group.exerciseName} (${groupExerciseTask})` : group.exerciseName}</h4>
                              {group.items.length > 1 && (
                                <div className="result-mini-chart" aria-label={`${group.exerciseName} fejlődési grafikon`}>
                                  {group.items.map((item) => {
                                    const value = Number(item.resultValue);
                                    const height = Number.isFinite(value) ? 22 + ((value - minValue) / span) * 58 : 22;
                                    return (
                                      <span
                                        key={item.id}
                                        style={{ height: `${height}%` }}
                                        title={formatResultValue(item.resultValue, group.resultUnit)}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                              <ul className="profile-training-list">
                                {group.items.map((item) => (
                                  <li className="profile-training-item" key={item.id}>
                                    <div className="profile-training-date">{formatTrainingDate(item.trainingDate)}</div>
                                    <div className="profile-training-meta">
                                      {formatResultValue(item.resultValue, group.resultUnit)} - {formatRegistrationTimestamp(item.recordedAt)}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  <section className="results-panel">
                    <h3>Ranglisták</h3>
                    <div className="leaderboard-controls">
                      <label className="admin-field">
                        <span>Gyakorlat</span>
                        <select
                          value={leaderboardForm.exerciseId}
                          onChange={(event) => handleLeaderboardFormChange('exerciseId', event.target.value)}
                          disabled={exercisesLoading || exercises.length === 0}
                        >
                          {exercises.length === 0 ? (
                            <option value="">Nincs elérhető gyakorlat</option>
                          ) : (
                            exercises.map((exercise) => (
                              <option key={exercise.id} value={exercise.id}>
                                {formatExerciseTask(exercise) ? `${exercise.name} (${formatExerciseTask(exercise)})` : exercise.name}
                              </option>
                            ))
                          )}
                        </select>
                      </label>
                      <label className="admin-field">
                        <span>Edzés ranglista</span>
                        <select
                          value={leaderboardForm.trainingDate}
                          onChange={(event) => handleLeaderboardFormChange('trainingDate', event.target.value)}
                          disabled={selectableTrainingDates.length === 0}
                        >
                          {selectableTrainingDates.length === 0 ? (
                            <option value="">Nincs edzés jelentkezés</option>
                          ) : (
                            selectableTrainingDates.map((registration) => (
                              <option key={registration.id} value={registration.trainingDate}>
                                {formatTrainingDate(registration.trainingDate)}
                              </option>
                            ))
                          )}
                        </select>
                      </label>
                    </div>

                    {!selectedLeaderboardExercise ? (
                      <p className="profile-muted">Válassz gyakorlatot a ranglistához.</p>
                    ) : (
                      <>
                        <h4>
                          {formatExerciseTask(selectedLeaderboardExercise)
                            ? `${selectedLeaderboardExercise.name} (${formatExerciseTask(selectedLeaderboardExercise)})`
                            : selectedLeaderboardExercise.name}
                          {' - '}
                          {selectedLeaderboardTrainingDate ? formatTrainingDate(selectedLeaderboardTrainingDate) : 'Nincs kiválasztott edzés'}
                        </h4>
                        {!selectedLeaderboardTrainingDate ? (
                          <p className="profile-muted">Válassz edzést az edzésenkénti ranglistához.</p>
                        ) : trainingLeaderboardRows.length === 0 ? (
                          <p className="profile-muted">Ezen az edzésen még nincs eredmény ehhez a gyakorlathoz.</p>
                        ) : (
                          <ol className="leaderboard-list">
                            {trainingLeaderboardRows.map((item) => (
                              <li key={item.id}>
                                <span>{item.userDisplayName || item.userEmail || 'Ismeretlen játékos'}</span>
                                <strong>{formatResultValue(item.resultValue, item.resultUnit || selectedLeaderboardExercise.resultUnit)}</strong>
                                <small>{formatRegistrationTimestamp(item.recordedAt)}</small>
                              </li>
                            ))}
                          </ol>
                        )}

                        <h4>All time</h4>
                        {allTimeRecords.length === 0 ? (
                          <p className="profile-muted">Még nincs all-time rekord ehhez a gyakorlathoz.</p>
                        ) : (
                          <ol className="leaderboard-list">
                            {allTimeRecords.map((record) => (
                              <li key={record.id}>
                                <span>{getPublicPlayerName(record)}</span>
                                <strong>{formatResultValue(record.resultValue, record.resultUnit || selectedLeaderboardExercise.resultUnit)}</strong>
                                <small>{formatRegistrationTimestamp(record.achievedAt)}</small>
                              </li>
                            ))}
                          </ol>
                        )}
                      </>
                    )}
                  </section>
                </div>
                  </>
                ) : (
                  <div className="squash-sets-view">
                    <section className="results-panel squash-head-to-head-panel">
                      <h3>Egymás elleni állás</h3>
                      {squashSetsLoading ? (
                        <p className="profile-muted">Összesített állás betöltése...</p>
                      ) : squashHeadToHeadRows.length === 0 ? (
                        <p className="profile-muted">Még nincs rögzített szetted.</p>
                      ) : (
                        <ul className="squash-head-to-head-list">
                          {squashHeadToHeadRows.map((row) => (
                            <li key={row.opponentId}>
                              <div className="squash-head-to-head-score">
                                <span>{row.currentPlayerName}</span>
                                <strong>{row.currentPlayerWins}–{row.opponentWins}</strong>
                                <span>{row.opponentName}</span>
                              </div>
                              <div className="squash-head-to-head-average">
                                <span>{formatSquashPointAverage(row.currentPlayerPointAverage)}</span>
                                <small>3 havi pontátlag</small>
                                <span>{formatSquashPointAverage(row.opponentPointAverage)}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    <section className="results-panel squash-set-entry-panel">
                      <div className="admin-content-section-head">
                        <h3>Szett rögzítése</h3>
                        {squashTrainingDate && <p>{formatTrainingDate(squashTrainingDate)}</p>}
                      </div>

                      {squashSetNotice && (
                        <p className={`registration-notice ${squashSetNotice.startsWith('Szett eredmények mentve') || squashSetNotice.startsWith('Szett eredménye frissítve') || squashSetNotice.startsWith('Szett eredménye törölve') ? 'ok' : 'error'}`}>
                          {squashSetNotice}
                        </p>
                      )}

                      {squashOpponentsLoading ? (
                        <p className="profile-muted">Mai ellenfelek betöltése...</p>
                      ) : !squashOpponentConfirmed ? (
                        <>
                          {squashOpponents.length === 0 ? (
                            <p className="profile-muted">Ma nincs választható ellenfél.</p>
                          ) : (
                            <div className="squash-opponent-list" role="radiogroup" aria-label="Ellenfél választása">
                              {squashOpponents.map((opponent) => (
                                <button
                                  type="button"
                                  role="radio"
                                  aria-checked={selectedSquashOpponentId === opponent.id}
                                  className={selectedSquashOpponentId === opponent.id ? 'selected' : ''}
                                  key={opponent.id}
                                  onClick={() => handleSelectSquashOpponent(opponent.id)}
                                >
                                  <span>{opponent.publicName || opponent.nickname || opponent.displayName}</span>
                                  {opponent.nickname && opponent.displayName && <small>{opponent.displayName}</small>}
                                </button>
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            className="button-primary squash-opponent-confirm"
                            onClick={handleConfirmSquashOpponent}
                            disabled={!selectedSquashOpponent}
                          >
                            <Check size={17} />
                            Ellenfél jóváhagyása
                          </button>
                        </>
                      ) : (
                        <div className="squash-set-recorder">
                          <div className="squash-matchup-head">
                            <h4>
                              {squashCurrentPlayer?.publicName || squashCurrentPlayer?.nickname || squashCurrentPlayer?.displayName}
                              {' v '}
                              {selectedSquashOpponent?.publicName || selectedSquashOpponent?.nickname || selectedSquashOpponent?.displayName}
                            </h4>
                            <button type="button" className="button-soft" onClick={handleChangeSquashOpponent}>
                              Másik ellenfél
                            </button>
                          </div>

                          <div className="squash-set-draft-list">
                            {squashSetDrafts.map((setDraft, index) => (
                              <section className="squash-set-draft" key={setDraft.id}>
                                <h5>{index + 1}. szett</h5>
                                <SquashScoreEditor
                                  playerAId={currentUser.uid}
                                  playerAName={squashCurrentPlayer?.publicName || squashCurrentPlayer?.displayName || 'Saját játékos'}
                                  playerBId={selectedSquashOpponent.id}
                                  playerBName={selectedSquashOpponent.publicName || selectedSquashOpponent.displayName}
                                  winnerId={setDraft.winnerId}
                                  loserScore={setDraft.loserScore}
                                  onWinnerChange={(winnerId) => handleSquashSetDraftChange(setDraft.id, 'winnerId', winnerId)}
                                  onLoserScoreChange={(score) => handleSquashSetDraftChange(setDraft.id, 'loserScore', score)}
                                  onRemove={squashSetDrafts.length > 1 ? () => handleRemoveSquashSetDraft(setDraft.id) : null}
                                  disabled={squashSetSaveBusy}
                                />
                              </section>
                            ))}
                          </div>

                          {squashSetDraftsComplete && (
                            <button type="button" className="button-soft squash-add-set" onClick={handleAddSquashSetDraft}>
                              <Plus size={17} />
                              Új szett
                            </button>
                          )}
                          <button
                            type="button"
                            className="button-primary squash-save-sets"
                            onClick={handleSaveSquashSets}
                            disabled={!squashSetDraftsComplete || squashSetSaveBusy}
                          >
                            {squashSetSaveBusy ? 'Mentés...' : 'Mentés'}
                          </button>
                        </div>
                      )}
                    </section>

                    <section className="results-panel squash-set-history-panel">
                      <h3>Saját szettek</h3>
                      {squashSetsLoading ? (
                        <p className="profile-muted">Szett eredmények betöltése...</p>
                      ) : squashSets.length === 0 ? (
                        <p className="profile-muted">Még nincs rögzített szett eredményed.</p>
                      ) : (
                        <div className="squash-set-history-list">
                          {squashSets.map((set) => {
                            const playerAName = getSquashSetPlayerName(set, set.playerAId);
                            const playerBName = getSquashSetPlayerName(set, set.playerBId);
                            const editDraft = squashSetEditDrafts[set.id];
                            const canEditToday = set.trainingDate === getBudapestTodayKey();

                            return (
                              <article className="squash-set-history-item" key={set.id}>
                                <div className="squash-set-history-head">
                                  <div>
                                    <strong>{playerAName} {set.playerAScore}–{set.playerBScore} {playerBName}</strong>
                                    <small>{formatTrainingDate(set.trainingDate)} · {formatRegistrationTimestamp(set.recordedAt)}</small>
                                  </div>
                                  <div className="squash-set-history-actions">
                                    {canEditToday && !editDraft && (
                                      <button
                                        type="button"
                                        className="button-soft admin-icon-button"
                                        onClick={() => handleStartSquashSetEdit(set)}
                                        disabled={Boolean(squashSetBusyId)}
                                        aria-label="Szett szerkesztése"
                                        title="Szett szerkesztése"
                                      >
                                        <Pencil size={16} />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="button-soft admin-icon-button danger"
                                      onClick={() => handleDeleteSquashSet(set)}
                                      disabled={Boolean(squashSetBusyId)}
                                      aria-label="Szett törlése"
                                      title="Szett törlése"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </div>

                                {editDraft && (
                                  <div className="squash-set-inline-edit">
                                    <SquashScoreEditor
                                      playerAId={set.playerAId}
                                      playerAName={playerAName}
                                      playerBId={set.playerBId}
                                      playerBName={playerBName}
                                      winnerId={editDraft.winnerId}
                                      loserScore={editDraft.loserScore}
                                      onWinnerChange={(winnerId) => handleSquashSetEditChange(set.id, 'winnerId', winnerId)}
                                      onLoserScoreChange={(score) => handleSquashSetEditChange(set.id, 'loserScore', score)}
                                      disabled={squashSetBusyId === set.id}
                                    />
                                    <div className="squash-set-inline-actions">
                                      <button type="button" className="button-soft" onClick={() => handleCancelSquashSetEdit(set.id)}>
                                        Mégse
                                      </button>
                                      <button
                                        type="button"
                                        className="button-primary"
                                        onClick={() => handleSaveSquashSetEdit(set)}
                                        disabled={squashSetBusyId === set.id}
                                      >
                                        {squashSetBusyId === set.id ? 'Mentés...' : 'Mentés'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  </div>
                )}
              </section>
            )}

            {billingPanelOpen && (
              <>
                <section className="card card-large profile-card profile-card-wide profile-billing-info">
                  <p>{billingInfoText}</p>
                </section>

                <section className="card card-large profile-card profile-card-wide">
                  <h2>Számlázási adatok</h2>
                  {billingLoading ? (
                    <p className="profile-muted">Számlázási adatok betöltése...</p>
                  ) : (
                    <>
                      <form
                        className="profile-address-form"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSaveBillingAddress();
                        }}
                      >
                        <label className="admin-field">
                          <span>Név</span>
                          <input
                            type="text"
                            value={billingForm.name}
                            onChange={(event) => handleBillingFieldChange('name', event.target.value)}
                            autoComplete="name"
                            required
                          />
                        </label>
                        <div className="profile-address-compact-row">
                          <label className="admin-field">
                            <span>Ország</span>
                            <input
                              type="text"
                              value={billingForm.country}
                              onChange={(event) => handleBillingFieldChange('country', event.target.value)}
                              autoComplete="country-name"
                              required
                            />
                          </label>
                          <label className="admin-field">
                            <span>Irányítószám</span>
                            <input
                              type="text"
                              value={billingForm.zip}
                              onChange={(event) => handleBillingFieldChange('zip', event.target.value)}
                              autoComplete="postal-code"
                              inputMode="numeric"
                              required
                            />
                          </label>
                        </div>
                        <label className="admin-field">
                          <span>Város</span>
                          <input
                            type="text"
                            value={billingForm.city}
                            onChange={(event) => handleBillingFieldChange('city', event.target.value)}
                            autoComplete="address-level2"
                            required
                          />
                        </label>
                        <label className="admin-field">
                          <span>Utca, házszám</span>
                          <input
                            type="text"
                            value={billingForm.street}
                            onChange={(event) => handleBillingFieldChange('street', event.target.value)}
                            autoComplete="address-line1"
                            required
                          />
                        </label>
                        <div className="profile-address-actions">
                          <button type="submit" className="button-primary" disabled={billingSaveBusy}>
                            {billingSaveBusy ? 'Mentés...' : 'Számlázási adatok mentése'}
                          </button>
                        </div>
                      </form>

                      {!hasAnyBillingData && <p className="profile-muted">Még nincs megadott számlázási adat.</p>}

                      {billingSaveNotice && (
                        <p className={`registration-notice ${billingSaveNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                          {billingSaveNotice}
                        </p>
                      )}
                    </>
                  )}
                </section>
              </>
            )}

            {notificationPanelOpen && (
              <section className="card card-large profile-card profile-card-wide">
                <div className="profile-section-head">
                  <h2>Értesítések</h2>
                </div>

                {emailPreferencesLoading ? (
                  <p className="profile-muted">Értesítések betöltése...</p>
                ) : (
                  <>
                    <form
                      className="profile-notification-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleSaveEmailPreferences();
                      }}
                    >
                      <div className="profile-notification-list">
                        {emailPreferenceOptions.map((option) => (
                          <label className="profile-notification-item" key={option.key}>
                            <span className="profile-notification-copy">
                              <strong>{option.title}</strong>
                              <span>{option.description}</span>
                            </span>
                            <span className="profile-notification-switch">
                              <input
                                type="checkbox"
                                checked={emailPreferences[option.key] !== false}
                                onChange={(event) => handleEmailPreferenceChange(option.key, event.target.checked)}
                              />
                              <span>{emailPreferences[option.key] !== false ? 'Bekapcsolva' : 'Kikapcsolva'}</span>
                            </span>
                          </label>
                        ))}
                      </div>

                      <div className="profile-address-actions">
                        <button type="submit" className="button-primary" disabled={emailPreferencesSaveBusy}>
                          {emailPreferencesSaveBusy ? 'Mentés...' : 'Értesítések mentése'}
                        </button>
                      </div>
                    </form>

                    {emailPreferencesNotice && (
                      <p className={`registration-notice ${emailPreferencesNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                        {emailPreferencesNotice}
                      </p>
                    )}
                  </>
                )}
              </section>
            )}

            {profileManagementPanelOpen && (
              <section className="card card-large profile-card profile-card-wide">
                <div className="profile-section-head">
                  <h2>Profil kezelés</h2>
                </div>

                <div className="profile-management-panel">
                  <div>
                    <strong>Jelszó módosítása</strong>
                    <p>
                      E-mailes regisztrációnál itt tudod lecserélni a jelszavadat.
                    </p>
                  </div>

                  {isPasswordUser(currentUser) ? (
                    <form className="profile-password-form" onSubmit={handleChangePassword}>
                      <label className="auth-field">
                        <span>Jelenlegi jelszó</span>
                        <input
                          className="auth-input"
                          type="password"
                          value={passwordChangeForm.currentPassword}
                          onChange={(event) => handlePasswordChangeFormChange('currentPassword', event.target.value)}
                          autoComplete="current-password"
                          required
                        />
                      </label>
                      <label className="auth-field">
                        <span>Új jelszó</span>
                        <input
                          className="auth-input"
                          type="password"
                          value={passwordChangeForm.nextPassword}
                          onChange={(event) => handlePasswordChangeFormChange('nextPassword', event.target.value)}
                          autoComplete="new-password"
                          required
                        />
                      </label>
                      <label className="auth-field">
                        <span>Új jelszó még egyszer</span>
                        <input
                          className="auth-input"
                          type="password"
                          value={passwordChangeForm.nextPasswordConfirm}
                          onChange={(event) => handlePasswordChangeFormChange('nextPasswordConfirm', event.target.value)}
                          autoComplete="new-password"
                          required
                        />
                      </label>
                      <p className="auth-helper">Minimum 10 karakter, kisbetű, nagybetű és szám.</p>
                      <div className="profile-address-actions">
                        <button type="submit" className="button-primary" disabled={passwordChangeBusy}>
                          {passwordChangeBusy ? 'Mentés...' : 'Jelszó módosítása'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className="profile-muted">
                      A jelszavadat a Google fiókodban tudod megváltoztatni.
                    </p>
                  )}

                  {passwordChangeNotice && (
                    <p className={`registration-notice ${passwordChangeNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                      {passwordChangeNotice}
                    </p>
                  )}
                </div>

                <div className="profile-delete-panel">
                  <div>
                    <strong>Profil törlése</strong>
                    <p>
                      A törlés után nem tudsz belépni ezzel a fiókkal. A jövőbeni edzésjelentkezéseid és várólistás helyeid törlődnek,
                      a profilhoz kötött egyéb adataid kikerülnek az adatbázisból, de a múltbeli edzéseken a neved és a korábbi rekordjaid megmaradnak.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="button-soft danger"
                    onClick={() => setAccountDeleteConfirmOpen(true)}
                    disabled={accountDeleteBusy}
                  >
                    <Trash2 size={16} />
                    Profil törlése
                  </button>
                </div>

                {accountDeleteNotice && <p className="registration-notice error">{accountDeleteNotice}</p>}
              </section>
            )}
          </>
        )}
      </main>

      {passwordManagedByGoogleNoticeOpen && (
        <div
          className="auth-overlay"
          onClick={() => setPasswordManagedByGoogleNoticeOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-google-password-notice-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setPasswordManagedByGoogleNoticeOpen(false)}
              aria-label="Bezárás"
            >
              <X size={18} />
            </button>
            <h2 id="profile-google-password-notice-title">A jelszót a Google kezeli</h2>
            <p>
              Ehhez az e-mail címhez Google-belépés tartozik, ezért a jelszót nem ez az oldal kezeli.
              A jelszavadat a Google fiókodban tudod megváltoztatni.
            </p>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="button-primary"
                onClick={() => setPasswordManagedByGoogleNoticeOpen(false)}
              >
                Rendben
              </button>
            </div>
          </div>
        </div>
      )}

      {accountDeleteConfirmOpen && (
        <div
          className="auth-overlay"
          onClick={() => !accountDeleteBusy && setAccountDeleteConfirmOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-delete-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setAccountDeleteConfirmOpen(false)}
              aria-label="Bezárás"
              disabled={accountDeleteBusy}
            >
              <X size={18} />
            </button>
            <h2 id="profile-delete-title">Profil törlése</h2>
            <p>
              Biztosan törlöd a profilodat? A jövőbeni jelentkezéseid és a profilhoz kötött egyéb adataid törlődnek,
              a múltbeli edzéseken és ranglistákon szereplő neved és eredményeid megmaradnak.
            </p>
            <div className="admin-confirm-training-date">{currentUser.displayName || currentUser.email}</div>
            <div className="admin-confirm-actions">
              <button className="button-soft" type="button" onClick={() => setAccountDeleteConfirmOpen(false)} disabled={accountDeleteBusy}>
                Mégsem
              </button>
              <button className="button-primary danger" type="button" onClick={handleDeleteOwnProfile} disabled={accountDeleteBusy}>
                {accountDeleteBusy ? 'Törlés...' : 'Profil törlése'}
              </button>
            </div>
          </div>
        </div>
      )}

      {lateCancelOpen && (
        <div className="auth-overlay" onClick={() => setLateCancelOpen(false)} role="dialog" aria-modal="true">
          <div className="late-cancel-modal card" onClick={(event) => event.stopPropagation()}>
            <button className="auth-close" onClick={() => setLateCancelOpen(false)} aria-label="Bezárás">
              <X size={18} />
            </button>
            <p>Lemondás az edzést megelőző nap 15:00 után 70%-os fizetéssel.</p>
            <button type="button" className="button-primary late-cancel-confirm" onClick={() => setLateCancelOpen(false)}>
              Rendben
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
